/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import clsx from 'clsx';
import React, { memo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import NotificationsIcon from '@mui/icons-material/Notifications';
import { AppBar, Badge, IconButton, Toolbar } from '@mui/material';

import { ProfileDropdown } from '../../components/ProfileDropdown/ProfileDropdown';
import { RootState } from '../../store';
import { useBadgeStyles } from '../../styles/badge';
import { CreateMenu } from './components/CreateMenu/CreateMenu';
import { NotificationMenu } from './components/NotificationMenu/NotificationMenu';
import { useHeaderLayoutStyles } from './HeaderLayout.styles';

enum MenuType {
  Notification = 'Notification',
}

const mapState = (state: RootState) => ({
  notifications: state.notifications,
  auth: state.auth,
});

const connector = connect(mapState);
type PropsFromRedux = ConnectedProps<typeof connector>;

type PrimaryAppBarProps = PropsFromRedux & {
  drawerOpen: boolean;
  setDrawerOpen: (state: boolean) => any;
  hideCreateMenu?: boolean;
  drawerWidth: number;
};

const HeaderLayoutComponent = memo<PrimaryAppBarProps>(props => {
  const badgeClasses = useBadgeStyles();
  const {
    notifications,
    drawerOpen,
    auth: { user },
    hideCreateMenu,
    drawerWidth,
  } = props;
  const classes = useHeaderLayoutStyles({ drawerWidth });

  const [notificationAnchorEl, setNotificationAnchorEl] = useState<HTMLElement | null>(null);

  const menuAssociation: Record<MenuType, React.Dispatch<React.SetStateAction<HTMLElement | null>>> = {
    [MenuType.Notification]: setNotificationAnchorEl,
  };

  function handleMenuOpen(menuType: MenuType, event: React.MouseEvent<any, MouseEvent>) {
    Object.keys(menuAssociation).forEach(key => {
      const curMenuType = key as unknown as MenuType;
      const setStateAction = menuAssociation[curMenuType];

      if (curMenuType === menuType) {
        setStateAction(event.currentTarget);
      } else {
        setStateAction(null);
      }
    });
  }

  function handleMenuClose() {
    Object.keys(menuAssociation).forEach(key => {
      const curMenuType = key as unknown as MenuType;
      const setStateAction = menuAssociation[curMenuType];

      setStateAction(null);
    });
  }

  const notificationsMenuId = 'primary-notification-menu';
  const renderNotificationsMenu = () => {
    return (
      <NotificationMenu id={notificationsMenuId} anchorEl={notificationAnchorEl} handleMenuClose={handleMenuClose} />
    );
  };

  return (
    <div>
      <AppBar
        position="fixed"
        className={clsx(classes.appBar, {
          [classes.appBarShift]: drawerOpen,
        })}
      >
        <Toolbar
          classes={{
            regular: classes.toolbarRegular,
            gutters: classes.toolbarGutters,
          }}
        >
          <div className={classes.createButtonContainer}>{!hideCreateMenu && <CreateMenu />}</div>

          <div className={classes.sectionDesktop}>
            <IconButton
              aria-label="Notifications"
              aria-controls={notificationsMenuId}
              aria-haspopup="true"
              onClick={event => handleMenuOpen(MenuType.Notification, event)}
              size="large"
            >
              <Badge
                classes={{ badge: badgeClasses.textBadge }}
                variant="dot"
                invisible={notifications.unreadCount === 0}
              >
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <ProfileDropdown name={`${user?.firstName || ''} ${user?.lastName || ''}`} user={user} />
          </div>
        </Toolbar>
      </AppBar>
      {renderNotificationsMenu()}
    </div>
  );
});

export const HeaderLayout = connector(HeaderLayoutComponent);
